import React, { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { ActivityLogEntry } from '@trustero/trustero-api-web/lib/activity/activity_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useContent } from 'src/context/hooks'
import { Overview } from 'src/xgenerated'
import { Gravatar } from '../../components/Gravatar'
import { formatISODateTime } from '../../Utils/formatDate'
import { useUsers } from '../../components/async/Users/useUsers'
import { getCitationUrl } from '../../pages/Activities/ActivitiesPage.constants'
import { CitationPillList } from '../../pages/Activities/CitationPillList'
import { getActivityActorLabel } from '../../pages/Activities/activityActor'
import { useCitationUrlOptions } from '../../pages/Activities/useCitationUrlOptions'
import {
  LogActor,
  LogDetails,
  LogItemContainer,
  LogText,
  LogTime,
} from './Changelog.styles'
import { Subject } from './Subject'

export const useActorName = (actor: string, type: number): string => {
  const { data } = useUsers()
  const { usersList } = data?.toObject() || { usersList: [] }
  const user = usersList.find((user) => user.email === actor)
  return getActivityActorLabel(actor, type, user?.name)
}

export type LogItemProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logItem: ActivityLogEntry | any
  borderBottom?: boolean
}

export const LogItem = ({
  logItem,
  borderBottom,
}: LogItemProps): ReactElement => {
  const { pageContext } = useParams<{ pageContext?: string }>()

  const isProto = typeof logItem?.getActor === 'function'

  const actor = isProto ? logItem.getActor() : logItem.actor
  const actorType = isProto
    ? logItem.getActorType()
    : logItem.actorType !== undefined
    ? logItem.actorType
    : logItem.actortype
  const message = isProto ? logItem.getMessage() : logItem.message
  const createdAtMs = isProto
    ? (logItem.getCreatedAt()?.getSeconds() || 0) * 1000 +
      Math.floor((logItem.getCreatedAt()?.getNanos() || 0) / 1e6)
    : logItem.createdat || 0

  const actorName = useActorName(actor, actorType)
  const citationUrlOptions = useCitationUrlOptions()

  const protoCitations = isProto ? logItem.getCitationsList() || [] : []
  const citations = isProto
    ? protoCitations.map(
        (cit: {
          getDescription(): string
          getCitationType(): number
          getCitationId(): string
        }) => ({
          text: cit.getDescription(),
          url: getCitationUrl(
            pageContext || '',
            cit.getCitationType(),
            cit.getCitationId(),
            citationUrlOptions,
          ),
        }),
      )
    : logItem.citations || []

  const content = useContent()
  if (!isProto && logItem.subjectmodeltype === MODEL_TYPE.OVERVIEW) {
    content.overviews.forEach((overview: Overview) => {
      if (overview.id !== logItem.subjectmodelid) return
      logItem.subjectmodelname = new StringValue()
        .setValue(overview.name)
        .toObject()
    })
  }

  return (
    <LogItemContainer borderBottom={borderBottom}>
      <Gravatar email={actor} emptyIsUnassigned={false} />
      <LogDetails>
        <LogActor>{actorName}</LogActor>
        <LogText>{message}</LogText>
        {citations.length > 0 ? (
          <CitationPillList citations={citations} />
        ) : !isProto ? (
          <Subject
            actorName={actorName}
            subjectModelName={logItem.subjectmodelname?.value}
            verb={logItem.message?.split(' ')?.[0] || ''}
            message={logItem.message}
            modelId={logItem.subjectmodelid}
            modelType={logItem.subjectmodeltype}
            attachmentId={logItem.attachmentid}
            attachmentType={logItem.attachmenttype}
          />
        ) : null}
        <LogTime>{formatISODateTime(createdAtMs)}</LogTime>
      </LogDetails>
    </LogItemContainer>
  )
}
